import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/auth';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    classes: 0,
    students: 0,
    pendingGrades: 0,
    todayAbsences: 0,
    upcomingHomework: 0,
  });
  const [recentClasses, setRecentClasses] = useState([]);
  const [upcomingHomeworks, setUpcomingHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Stats + classes
      const res = await api.get('/teacher/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data.stats || {});
      setRecentClasses(res.data.classes || []);

      // Devoirs
      try {
        const hwRes = await api.get('/teacher/homework', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = Array.isArray(hwRes.data) ? hwRes.data : (hwRes.data.data || []);
        setUpcomingHomeworks(list.slice(0, 3));
      } catch (e) {
        setUpcomingHomeworks([]);
      }
    } catch (err) {
      console.error('Erreur dashboard prof:', err);
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.name
    ? user.name.split(' ')[0].charAt(0).toUpperCase() + user.name.split(' ')[0].slice(1).toLowerCase()
    : 'Professeur';

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">

      {/* ═══════════ BANNIÈRE HAUT ═══════════ */}
      <div className="relative rounded-2xl overflow-hidden mb-6 h-48 lg:h-56 bg-gradient-to-r from-[#0A0A0A] to-[#F39C12]">

        <img
          src="/images/banner-teacher.png"
          alt="Professeur"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent"></div>

        <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-center max-w-xl">
          <h1 className="text-white text-3xl lg:text-4xl font-bold">
            Bonjour Prof {firstName} 👋
          </h1>
          <p className="text-white/95 text-base lg:text-lg mt-3">
            Voici un aperçu de votre activité d'enseignement.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">Chargement...</div>
      ) : (
        <>
          {/* ═══════════ 4 CARTES STATS ═══════════ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={<ClassesIcon className="w-8 h-8 text-white" />}
              bg="bg-[#3B82F6]"
              value={stats.classes || 0}
              label="Classes"
              linkTo="/teacher/classes"
            />
            <StatCard
              icon={<UsersIcon className="w-8 h-8 text-white" />}
              bg="bg-[#1E40AF]"
              value={stats.students || 0}
              label="Élèves"
              linkTo="/teacher/students"
            />
            <StatCard
              icon={<PenIcon className="w-8 h-8 text-white" />}
              bg="bg-[#22C55E]"
              value={stats.pendingGrades || 0}
              label="Notes à saisir"
              linkTo="/teacher/grades"
            />
            <StatCard
              icon={<BookIcon className="w-8 h-8 text-white" />}
              bg="bg-[#A855F7]"
              value={stats.upcomingHomework || 0}
              label="Devoirs à venir"
              linkTo="/teacher/homework"
            />
          </div>

          {/* ═══════════ 2 COLONNES : MES CLASSES + DEVOIRS ═══════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

            {/* Mes classes (2/3) */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Mes classes</h3>
                <Link
                  to="/teacher/classes"
                  className="text-[#F39C12] hover:text-[#E67E22] text-base font-medium"
                >
                  Voir toutes →
                </Link>
              </div>

              {recentClasses.length === 0 ? (
                <p className="text-gray-500 text-center py-8 text-base">
                  Aucune classe assignée pour le moment
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {recentClasses.map((cls) => (
                    <Link
                      key={cls.id}
                      to={`/teacher/classes/${cls.id}/students`}
                      className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-[#F39C12]/30 hover:shadow-md transition group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#F39C12]/10 flex items-center justify-center">
                          🏫
                        </div>
                        <div>
                          <p className="font-bold text-[#0A0A0A] text-base">{cls.name}</p>
                          <p className="text-xs text-gray-500">
                            {cls.students_count || 0} élève{cls.students_count > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <span className="text-gray-400 group-hover:text-[#F39C12] group-hover:translate-x-1 transition">
                        →
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Prochains devoirs (1/3) */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Prochains devoirs</h3>
              </div>

              {upcomingHomeworks.length === 0 ? (
                <p className="text-gray-500 text-center py-8 text-sm">
                  Aucun devoir planifié
                </p>
              ) : (
                <div className="space-y-3">
                  {upcomingHomeworks.map((hw) => (
                    <Link
                      key={hw.id}
                      to="/teacher/homework"
                      className="block p-3 rounded-xl bg-gray-50 hover:bg-[#FFF3E0] transition"
                    >
                      <p className="font-bold text-sm text-[#0A0A0A]">📚 {hw.subject}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{hw.class_name}</p>
                      <p className="text-xs text-[#F39C12] mt-1">
                        À rendre le {hw.due_date}
                      </p>
                    </Link>
                  ))}
                </div>
              )}

              {/* Info bas */}
              <div className="mt-6 bg-orange-50 rounded-xl p-4 flex items-start gap-3">
                <span className="text-xl">💡</span>
                <p className="text-xs text-gray-700 leading-relaxed">
                  Pensez à faire l'appel et à saisir vos notes régulièrement.
                </p>
              </div>
            </div>
          </div>

          {/* ═══════════ RACCOURCIS ═══════════ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <ShortcutButton icon="📋" label="Faire l'appel" to="/teacher/attendance" />
            <ShortcutButton icon="✏️" label="Saisir les notes" to="/teacher/grades" />
            <ShortcutButton icon="📚" label="Planifier un devoir" to="/teacher/homework" />
            <ShortcutButton icon="📊" label="Voir les résultats" to="/teacher/results" />
          </div>

          {/* ═══════════ BANNIÈRE BAS ═══════════ */}
          <div className="bg-orange-50 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#F39C12] flex items-center justify-center flex-shrink-0 text-white text-2xl">
              💬
            </div>
            <div>
              <p className="font-bold text-[#0A0A0A] text-base">L'éducation est l'arme la plus puissante</p>
              <p className="text-gray-600 text-sm">
                pour changer le monde.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// COMPOSANTS
// ═══════════════════════════════════════════════════════════════════

function StatCard({ icon, bg, value, label, linkTo }) {
  return (
    <Link
      to={linkTo}
      className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg hover:border-[#F39C12]/30 transition-all duration-300 group"
    >
      <div className={`w-14 h-14 rounded-xl ${bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
      <p className="text-4xl font-extrabold text-[#0A0A0A]">{value}</p>
      <p className="text-base text-gray-500 mt-2">{label}</p>
    </Link>
  );
}

function ShortcutButton({ icon, label, to }) {
  return (
    <Link
      to={to}
      className="bg-[#FFF3E0] hover:bg-[#FFE7CC] rounded-2xl p-5 flex items-center justify-between transition-all duration-300 hover:shadow-md group"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg bg-[#F39C12] flex items-center justify-center text-lg text-white">
          {icon}
        </div>
        <span className="font-bold text-[#0A0A0A] text-sm">{label}</span>
      </div>
      <span className="text-[#F39C12] group-hover:translate-x-1 transition">→</span>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ICÔNES SVG
// ═══════════════════════════════════════════════════════════════════

const ClassesIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M22 10L12 5 2 10l10 5 10-5z" />
    <path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const PenIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const BookIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

export default TeacherDashboard;